import type { Selectable } from "kysely"
import { z } from "zod"

import type { DB } from "@generated/types"
import { isFeatureFlagKey, type FeatureFlagKey } from "~/features/feature-flags/shared/registry"

/**
 * Flag targets, changes, overrides and history as client and server exchange them.
 * A change must name a registry flag; stored rows and history can outlive one.
 */

const OVERRIDE_SCOPES = ["default", "org", "user"] as const
export type OverrideScope = (typeof OVERRIDE_SCOPES)[number]

/** What an override applies to. `targetId` is "" for `default`, the Auth0 org id for `org` and
 * the Auth0 user id (`sub`) for `user`. */
export type FlagTarget = {
  flagKey: FeatureFlagKey
  scope: OverrideScope
  targetId: string
}

const flagKeySchema = z
  .string()
  .refine(isFeatureFlagKey, (key) => ({ message: `Unknown flag "${key}".` }))

/** `value` sets the override (true/false) or removes it (null). `expected` is the stored value
 * (null: no override) the requester saw, so a change made from stale data is rejected instead of
 * overwriting a colleague's. */
const changeFields = { value: z.boolean().nullable(), expected: z.boolean().nullable() }

export const flagChangeSchema = z.discriminatedUnion("scope", [
  z.object({
    flagKey: flagKeySchema,
    scope: z.literal("default"),
    targetId: z.literal(""),
    ...changeFields,
  }),
  z.object({
    flagKey: flagKeySchema,
    scope: z.enum(["org", "user"]),
    targetId: z.string().min(1),
    ...changeFields,
  }),
])
export type FlagChangeRequest = z.infer<typeof flagChangeSchema>

export type FlagOverride = FlagTarget & { value: boolean }

/** A stored override, with who set it. Its flag may no longer exist. */
export type StoredFlagOverride = Selectable<DB["FeatureFlagOverride"]>

/** A stored override of a registry flag, as `knownFlagRows` returns it. */
export type FlagOverrideRow = StoredFlagOverride & { flagKey: FeatureFlagKey }

/** One map key from several ids, none of which can contain the separator. */
export const compositeKey = (...parts: readonly string[]) => parts.join("\u0000")

/** A key for maps and sets of targets. */
export const targetKey = (target: Pick<StoredFlagOverride, "flagKey" | "scope" | "targetId">) =>
  compositeKey(target.flagKey, target.scope, target.targetId)

/** A batch of changes applied together. */
export const flagBatchSchema = z.object({
  changes: z
    .array(flagChangeSchema)
    .min(1)
    .max(1000)
    .refine(
      (changes) => new Set(changes.map(targetKey)).size === changes.length,
      "A batch can change each override only once.",
    ),
  note: z.string().trim().max(500).nullish(),
})

/** One entry of the change history. Its flag may no longer exist. */
export type FlagHistoryRow = Selectable<DB["FeatureFlagChange"]>

/** `changedBy` and `updatedBy` of what the LaunchDarkly sync writes. */
export const LAUNCHDARKLY_SYNC_AUTHOR = "launchdarkly-sync"

/** The most changes one history request returns. */
export const HISTORY_LIMIT = 500

export const flagHistoryFilterSchema = z.object({ flagKey: z.string().optional() })

/** The Auth0 org ids each custom flag is built for. */
export type CustomFlagCompanies = Readonly<Partial<Record<FeatureFlagKey, readonly string[]>>>

/** A user the panel names, with the companies they belong to. */
export type DirectoryUser = {
  id: string
  name: string
  email: string
  orgIds: string[]
  isStaff: boolean
}

/** Rows whose flag is in the registry: drops those of removed flags. */
export const knownFlagRows = <T extends { flagKey: string }>(rows: readonly T[]) =>
  rows.filter((row): row is T & { flagKey: FeatureFlagKey } => isFeatureFlagKey(row.flagKey))
